import PropTypes from "prop-types";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { OrganisationContext, UserContext } from "../../../layouts/MainLayout";

/**Third Part Libraries */
import classNames from "classnames";
import { Accordion } from "react-bootstrap";
import Card from "react-bootstrap/Card";
import { useTranslation } from "react-i18next";

/**Custom Components**/
import EmptyScreen from "../../../utils/components/common/EmptyScreen/EmptyScreen";
import LoadingSpinner from "../../../utils/components/common/loadingSpinner/LoadingSpinner";
import MultiSelectDropdown from "../../../utils/components/common/multiSelectDropdown/MultiSelectDropdown";
import Pagination from "../../../utils/components/common/pagination";
import Icons from "../../../utils/components/icons";

/**Services and generic functions**/
import {
  getDeviceDiagonosticReportdata,
  getDeviceParamReportdata,
} from "../../../services/ReportService";
import { formatDateTime, handleTableColSort } from "../../../utils/utils";

/**Style**/
import "../ReportDetails.scss";
import {
  columListDiagnosticts,
  columListParameter,
} from "../ReportsDetailsConstants";
import { getModeValue } from "../../../utils/constants/deviceConstants";

const cartridgeLevelValues = {
  "20AndAbove": [20, 100],
  "10to19": [10, 19],
  below10: [0, 9],
};
const recordsPerPage = 9;

const ReportListViewTab = ({
  CustomToggle,
  setActiveAccordion,
  activeAccordion,
  selectedDevice,
  updateSelectedFilters,
  updateReportData,
  durationErr,
  selectedDuration,
  updateRefreshTime,
  currentRoute,
}) => {
  const { t } = useTranslation("reports");
  const { featureEnabledStatus, userCognitoData } = useContext(UserContext);
  const { siteData, orgData } = useContext(OrganisationContext);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageTotalCount, setPageTotalCount] = useState();

  const [err, setErr] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState();

  const handlePageChange = useCallback((item) => {
    setCurrentPage(item + 1);
  }, []);

  /**Sorting Starts */
  const [columList, setColumList] = useState(
    currentRoute === "device-diagnostics"
      ? columListDiagnosticts
      : columListParameter
  );

  const selectedSortList = useMemo(() => {
    const transformedArray = columList
      .filter((item) => item.val !== "")
      .map((item) => ({ [item.key]: item.val }));

    return transformedArray;
  }, [columList]);

  // Two conditions for For "device-diagnostics" and "non-device-diagnostics" report
  const sortLabel =
    currentRoute === "device-diagnostics"
      ? { asc: "Ascending sort", desc: "Descending sort", "": "No sort" }
      : { asc: "Ascending sort", desc: "Descending sort" };

  const handleSort = (key) => {
    const updatedColList = columList.map((col) => {
      if (col.key !== key) return col; // Return unmodified if it's not the column being sorted

      // Apply different sorting logic based on the route
      if (currentRoute !== "device-diagnostics") {
        // For routes other than "device-diagnostics", toggle between "asc" and "desc"
        return { ...col, val: col.val === "asc" ? "desc" : "asc" };
      } else {
        // For "device-diagnostics", toggle between "asc", "desc", "No sort"
        let newVal = "";
        if (col.val === "") newVal = "desc";
        else if (col.val === "desc") newVal = "asc";
        else if (col.val === "asc") newVal = "";
        return { ...col, val: newVal };
      }
    });
    setColumList(updatedColList);
  };

  /**Sorting ends */

  const showEmptyMsg = useMemo(() => {
    const showMsg =
      !isLoading &&
      (err ||
        durationErr.isErr ||
        !selectedDevice?.length ||
        !reportData?.length);

    return showMsg;
  }, [err, durationErr.isErr, selectedDevice, reportData, isLoading]);

  const emptyMsg = useMemo(() => {
    if (durationErr.isErr) {
      return {
        title: t("no_data_to_display"),
        desc: durationErr.errMsg,
      };
    }
    if (!selectedDevice?.length) {
      return {
        title: t("no_data_to_display"),
        desc:
          currentRoute === "device-diagnostics"
            ? t("noDataMsg.deviceDiagnostics")
            : t("noDataMsg.deviceParameterList"),
      };
    }
    if (err) {
      return {
        title: t("no_data_to_display"),
        desc: t("downloadsFailed"),
      };
    }
    if (!reportData?.length) {
      return {
        title: t("errMsg1"),
        desc: t("errMsg2"),
      };
    }
    return {
      title: t("no_data_to_display"),
      desc: t("downloadsFailed"),
    };
  }, [
    durationErr.isErr,
    durationErr.errMsg,
    selectedDevice?.length,
    err,
    reportData?.length,
    t,
    currentRoute,
  ]);

  /**Dropdown options starts**/
  const ppbFilterOption = [
    {
      name: t("reportFilter.deviceDiagnostics.beyond150"),
      id: "Beyond150PctOfTarget",
    },
    {
      name: t("reportFilter.deviceDiagnostics.beyond130"),
      id: "Beyond130PctOfTarget",
    },
    {
      name: t("reportFilter.deviceDiagnostics.within30"),
      id: "Within30PctOfTarget",
    },
    {
      name: t("reportFilter.deviceDiagnostics.below70"),
      id: "Below70PctOfTarget",
    },
  ];
  const modeFilterOption = useMemo(() => {
    const modeList = [
      {
        name: t("reportFilter.deviceDiagnostics.operational"),
        id: "OPERATIONAL",
      },
      { name: t("reportFilter.deviceDiagnostics.senseOnly"), id: "SENSE_ONLY" },
      { name: t("reportFilter.deviceDiagnostics.standby"), id: "STANDBY" },
    ];
    if (featureEnabledStatus.researchMode) {
      modeList.push({
        name: t("reportFilter.deviceDiagnostics.research"),
        id: "RESEARCH",
      });
    }
    return modeList;
  }, [featureEnabledStatus.researchMode, t]);

  const cartridgeLevelOption = [
    { name: t("reportFilter.deviceDiagnostics.20AndAbove"), id: "20AndAbove" },
    { name: t("reportFilter.deviceDiagnostics.10_19"), id: "10to19" },
    { name: t("reportFilter.deviceDiagnostics.below10"), id: "below10" },
  ];
  /**Dropdown options ends**/

  /**Dropdown default values starts**/
  const [ppbFilterVal, setPpbFilterVal] = useState({
    Beyond150PctOfTarget: false,
    Beyond130PctOfTarget: false,
    Within30PctOfTarget: false,
    Below70PctOfTarget: false,
  });
  const [modeFilterVal, setModeFilterVal] = useState([]);
  const [cartridgeVal, setCartridgeVal] = useState([]);

  /**Dropdown default values ends**/

  /**Callback functions for filter dropdowns starts**/
  /**
   * actionType: String [select | remove]
   * filterList: Array<Objects> [Updated list after select or remove]
   * itemUpdated: Object<{id:"", name:""}>  [Item select or removed]
   */
  const updatePpbFilter = useCallback((actionType, filterList, itemUpdated) => {
    const newItem = {
      [itemUpdated["id"]]: actionType === "select",
    };
    setPpbFilterVal((prevVal) => {
      return { ...prevVal, ...newItem };
    });
  }, []);

  const updateModeFilterVal = useCallback((filterList = []) => {
    const modeIds = filterList.map((data) => data.id);
    setModeFilterVal(modeIds);
  }, []);

  const updateCartridgeVal = useCallback((filterList = []) => {
    const cartridgeRange = filterList.map((data) => {
      return cartridgeLevelValues[data.id];
    });
    setCartridgeVal(cartridgeRange);
  }, []);
  /**Callback functions for filter dropdowns ends**/

  /** Sequence-2.1: API call for device diagnostics list report **/
  const getDeviceDiagonosticReportDetails = useCallback(
    (queryParams) => {
      setIsLoading(true);
      updateSelectedFilters(queryParams);
      getDeviceDiagonosticReportdata(queryParams)
        .then((response) => {
          const data = response?.data?.Items || [];
          setReportData([...data]);
          updateReportData([...data]);
          setPageTotalCount(
            Math.ceil(response.data.TotalTelemetryCount / recordsPerPage)
          );
          setErr(false);
          setIsLoading(false);
        })
        .catch((err) => {
          setIsLoading(false);
          setErr(true);
        });
    },
    [updateSelectedFilters, updateReportData]
  );

  /** Sequence-2.2: API call for device parameter list report **/
  const getDeviceParameterListData = useCallback(
    (queryParams) => {
      setIsLoading(true);
      updateSelectedFilters(queryParams);
      getDeviceParamReportdata(queryParams)
        .then((response) => {
          const data = response?.data?.Items || [];
          setReportData([...data]);
          updateReportData([...data]);
          setPageTotalCount(
            Math.ceil(response.data.TotalTelemetryCount / recordsPerPage)
          );
          setErr(false);
          setIsLoading(false);
        })
        .catch((err) => {
          setIsLoading(false);
          setErr(true);
        });
    },
    [updateSelectedFilters, updateReportData]
  );

  /** Sequence-1: UseEffect to set query Param for getdeviceDiagnosticsData **/
  useEffect(() => {
    const isDataComplete =
      siteData.id &&
      userCognitoData?.username &&
      orgData?.TenantId &&
      selectedDevice &&
      selectedDevice?.length &&
      !durationErr.isErr;
    if (!isDataComplete) {
      setReportData();
      return;
    }

    /**Filters for Query-Param */
    const filters = {
      clo2Value: { ...ppbFilterVal },
      timeStamp: {
        between: selectedDuration,
      },
      deviceId: selectedDevice,
    };
    if (modeFilterVal?.length) {
      filters.mode = modeFilterVal;
    }
    if (cartridgeVal?.length) {
      filters.cartridgeLevel = {
        between: cartridgeVal,
      };
    }
    const queryParams = {
      userId: userCognitoData?.username,
      tenantId: orgData?.TenantId,
      siteId: siteData.id,
      size: recordsPerPage,
      pageNo: currentPage,
      filters: filters,
      sort: selectedSortList,
    };
    if (currentRoute === "device-diagnostics") {
      getDeviceDiagonosticReportDetails(queryParams);
    } else {
      getDeviceParameterListData(queryParams);
    }
  }, [
    userCognitoData?.username,
    orgData?.TenantId,
    siteData.id,
    currentPage,
    selectedDevice,
    ppbFilterVal,
    modeFilterVal,
    cartridgeVal,
    selectedDuration,
    durationErr,
    getDeviceDiagonosticReportDetails,
    getDeviceParameterListData,
    selectedSortList,
    currentRoute,
  ]);

  useEffect(() => {
    updateRefreshTime(new Date());
  }, [
    selectedDuration,
    ppbFilterVal,
    selectedDevice,
    modeFilterVal,
    cartridgeVal,
    updateRefreshTime,
  ]);

  /** Formatter fucntion for table values starts*/
  /**
   * modeBasedValue:-
   * The App displays the Temperature and Humidity values as “-”
   * when the device is in Standby Mode and Offline Mode.
   */
  const modeBasedValue = useCallback((value, mode) => {
    if ((!value && value !== 0) || mode === "OFFLINE" || mode === "STANDBY")
      return "-";
    if (value === 0) return 0;
    return value.toFixed(2);
  }, []);

  const formateClo2Value = useCallback((ppb, mode) => {
    if ((!ppb && ppb !== 0) || mode === "OFFLINE" || mode === "STANDBY")
      return "-";

    return ppb > 0 ? ppb.toFixed(2) : 0;
  }, []);

  const getModeString = useCallback(
    (mode = "") => {
      const modeVal = getModeValue(mode);
      const str = t(`modes.${modeVal}`);
      return str;
    },
    [t]
  );

  const convertToFloat = useCallback((val) => {
    if (!val && val !== 0) return "-";
    return val ? parseFloat(val).toFixed(2) : 0;
  }, []);

  /**
   * Value: array : [n1, n2]
   * type: string : [voc || nox]
   */
  const formatSgpValue = useCallback((value, type) => {
    if (!value && value !== 0) return "-";
    return type === "voc" ? value[0] : value[1];
  }, []);

  const getGeneratedMode = useCallback((val) => {
    if (typeof val !== "boolean") return "-";
    return val ? "True" : "False";
  }, []);
  /** Formatter fucntion for table values ends*/

  const getTableData = useCallback(() => {
    let tableData;
    if (currentRoute === "device-diagnostics") {
      tableData = reportData.map((data, i) => {
        return (
          <tr key={`${data?.eventUUID}${i + 1}`}>
            {selectedDevice?.length > 1 && (
              <>
                {/*
                 * Note:- These values are not handled from cloud side.
                 * So, hardcoding some of them as per the discussion in the team
                 */}
                <td>{data?.DeviceName || "-"}</td>
                {/* Device UDI:  deviceUUIDFromTelemetry -> '-' in case key/value not found */}
                <td>{data?.deviceUUIDFromTelemetry || "-"}</td>
                {/* Device UUID: deviceUUIDFromTelemetry -> '-' in case key/value not found */}
                <td>{data?.deviceUUIDFromTelemetry || "-"}</td>
                {/* Thing Name:  PK [Without D#]  (OR) DeviceUUID -> You can use anyone as per convenience */}
                <td>{data?.PK ? data.PK.slice(2) : "-"}</td>
                {/* Model:  Model -> For now hard code it */}
                <td>{"Room"}</td>
              </>
            )}
            {/* EventUUID: eventUUID -> '-' in case key/value not found */}
            <td>{data?.eventUUID || "-"}</td>
            <td>{formateClo2Value(data?.clo2Value, data?.mode)}</td>
            <td>{getModeString(data?.mode)}</td>
            <td>{modeBasedValue(data?.temperature, data?.mode)}</td>
            <td>{modeBasedValue(data?.humidity, data?.mode)}</td>
            <td>{modeBasedValue(data?.barometricPressure, data?.mode)}</td>
            <td>
              {data?.cartridgeLevel || data?.cartridgeLevel === 0
                ? data.cartridgeLevel
                : "-"}
            </td>
            <td>{getGeneratedMode(data?.generated)}</td>
            <td>{convertToFloat(data?.lastShotSize)}</td>
            <td>{formatSgpValue(data?.sgp4x2, "voc")}</td>
            <td>{formatSgpValue(data?.sgp4x2, "nox")}</td>
            <td>{formatSgpValue(data?.sgp4x4, "voc")}</td>
            <td>{formatSgpValue(data?.sgp4x4, "nox")}</td>
            <td>{formatSgpValue(data?.sgp4x5, "voc")}</td>
            <td>{formatSgpValue(data?.sgp4x5, "nox")}</td>
            <td>{formatSgpValue(data?.sgp4x7, "voc")}</td>
            <td>{formatSgpValue(data?.sgp4x7, "nox")}</td>
            <td>{data?.target || data?.target === 0 ? data.target : "-"}</td>
            <td>
              {data?.timeStamp
                ? formatDateTime(new Date(data?.timeStamp), true, true)
                : "-"}
            </td>
          </tr>
        );
      });
    } else {
      tableData = reportData.map((data, i) => {
        return (
          <tr key={`${data?.deviceUUIDFromTelemetry}${i}`}>
            {selectedDevice?.length > 1 && (
              <>
                {/*
                 * Note:- These values are not handled from cloud side.
                 * So, hardcoding some of them as per the discussion in the team
                 */}
                <td>{data?.DeviceName || "-"}</td>
                {/* Device UDI:  deviceUUIDFromTelemetry -> '-' in case key/value not found */}
                <td>{data?.deviceUUIDFromTelemetry || "-"}</td>
              </>
            )}
            <td>{formateClo2Value(data?.clo2Value, data?.mode)}</td>
            <td>{getModeString(data?.mode)}</td>
            <td>
              {data?.cartridgeLevel || data?.cartridgeLevel === 0
                ? data.cartridgeLevel
                : "-"}
            </td>
            <td>{modeBasedValue(data?.temperature, data?.mode)}</td>
            <td>{modeBasedValue(data?.humidity, data?.mode)}</td>
            <td>{modeBasedValue(data?.barometricPressure, data?.mode)}</td>
            <td>
              {" "}
              {data?.timeStamp
                ? formatDateTime(new Date(data?.timeStamp), true, true)
                : "-"}
            </td>
          </tr>
        );
      });
    }
    return tableData;
  }, [
    getModeString,
    convertToFloat,
    currentRoute,
    formatSgpValue,
    formateClo2Value,
    getGeneratedMode,
    modeBasedValue,
    reportData,
    selectedDevice?.length,
  ]);

  return (
    <div className="report-list-view">
      <Accordion
        activeKey={activeAccordion?.listViewActive ? ["list-view"] : []}
      >
        <Card className="list-view-container">
          {/* Header View Starts */}
          <Card.Header>
            <div className="collapsed-header">
              {/* Left Header Starts */}
              {/* Left header Collapsed View Starts*/}
              <div
                className={`"d-flex gap-2 align-item-center" ${
                  activeAccordion.listViewActive ? "hide" : "show"
                }`}
              >
                {Icons.graphIcon}
                <label>
                  {currentRoute === "device-diagnostics"
                    ? t("deviceDiagnosticsSubHeading")
                    : t("deviceParameterListSubHeading")}
                </label>
              </div>
              {/* Left header Collapsed View Ends*/}
              {/* Left header Expand View Starts*/}
              <div
                className={`"d-flex gap-2 align-item-center" ${
                  activeAccordion.listViewActive ? "show" : "hide"
                }`}
              >
                <div className="dropdown">
                  <MultiSelectDropdown
                    dropdownOptions={ppbFilterOption}
                    placeholder={t("reportFilter.deviceDiagnostics.ppb")}
                    showCheckbox={true}
                    className="ppb-level-filter"
                    onValSelect={(selectedList, selectedItem) => {
                      updatePpbFilter("select", selectedList, selectedItem);
                    }}
                    onValRemove={(selectedList, removedItem) => {
                      updatePpbFilter("remove", selectedList, removedItem);
                    }}
                    iconName={"ppbIcon"}
                  />
                </div>
                <div className="dropdown">
                  <MultiSelectDropdown
                    dropdownOptions={modeFilterOption}
                    placeholder={t("reportFilter.deviceDiagnostics.mode")}
                    showCheckbox={true}
                    className="mode-filter"
                    onValSelect={(selectedList, selectedItem) => {
                      updateModeFilterVal(selectedList);
                    }}
                    onValRemove={(selectedList, removedItem) => {
                      updateModeFilterVal(selectedList);
                    }}
                    iconName={"nutIcon"}
                  />
                </div>
                <div className="dropdown">
                  <MultiSelectDropdown
                    dropdownOptions={cartridgeLevelOption}
                    placeholder={t("reportFilter.deviceDiagnostics.cartridge")}
                    showCheckbox={true}
                    className="cartridge-level-filter"
                    iconName={"testTubeIcon"}
                    onValSelect={(selectedList, selectedItem) => {
                      updateCartridgeVal(selectedList);
                    }}
                    onValRemove={(selectedList, removedItem) => {
                      updateCartridgeVal(selectedList);
                    }}
                  />
                </div>
              </div>
              {/* Left header Expand View Ends*/}
              {/* Left Header ends */}
              {/* Right Header Starts */}
              <div className="d-flex gap-2 align-item-center">
                <CustomToggle
                  eventKey="list-view"
                  customActiveAccordion={activeAccordion}
                  setCustomActiveAccordion={setActiveAccordion}
                  selectedDevice={selectedDevice}
                >
                  {activeAccordion.listViewActive
                    ? Icons.expandAccordion
                    : Icons.collapseAccordion}
                </CustomToggle>
              </div>
              {/* Right Header Starts */}
            </div>
          </Card.Header>
          {/* Header View Ends */}
          {/* Body Container : Table View Starts */}
          <Accordion.Collapse eventKey="list-view">
            <Card.Body>
              <div
                className={`data-container ${showEmptyMsg ? "empty-msg" : ""}`}
              >
                <table className="table">
                  <thead>
                    <tr>
                      {columList.map((item) => {
                        return (
                          <th
                            key={item.key}
                            className={`${
                              selectedDevice?.length > 1
                                ? ""
                                : item.singleDevice
                                ? ""
                                : "hide"
                            }${
                              (!selectedDevice || selectedDevice.length <= 1) &&
                              (currentRoute === "device-diagnostics"
                                ? item.key === "eventUuid"
                                  ? "pl35"
                                  : ""
                                : item.key === "clo2Value"
                                ? "pl35"
                                : "")
                            } `}
                          >
                            <div
                              className="sortIconWrapper d-flex justify-content-start list-view-sort"
                              id="listheader"
                              onClick={() => {
                                if (item.sort) {
                                  handleSort(item.key);
                                }
                              }}
                            >
                              <span
                                className="label"
                                title={t(item.label)}
                                id={item.key}
                              >
                                {t(item.label)}
                              </span>
                              {item.sort && (
                                <div
                                  className="sortIcons sorting"
                                  id={`sort${item.key}`}
                                  title={sortLabel[item.val]}
                                >
                                  <button
                                    type="button"
                                    id="sort_asc"
                                    className={classNames({
                                      active: item.val === "asc",
                                      ascbutton: "ascbutton",
                                    })}
                                  >
                                    {Icons.upArrowIcon}
                                  </button>
                                  <button
                                    type="button"
                                    id="sort_desc"
                                    className={classNames({
                                      active: item.val === "desc",
                                    })}
                                  >
                                    {Icons.downArrowIcon}
                                  </button>
                                </div>
                              )}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {showEmptyMsg ? (
                      <tr>
                        <td
                          colSpan={`${selectedDevice?.length > 1 ? 19 : 24}`}
                          className="empty-screen-td"
                        >
                          <div className="empty-screen-wrapper">
                            <EmptyScreen
                              img={Icons.emptyScreenReports}
                              small={true}
                              {...emptyMsg}
                            />
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <>{reportData ? getTableData() : <></>}</>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="bodyFooter">
                {!showEmptyMsg && (
                  <>
                    <Pagination
                      currentPage={currentPage - 1}
                      totalPages={pageTotalCount}
                      pageCallBack={handlePageChange}
                    />
                  </>
                )}
              </div>
            </Card.Body>
          </Accordion.Collapse>
          {/* Body Container : Table View Ends */}
        </Card>
      </Accordion>
      {isLoading && <LoadingSpinner />}
    </div>
  );
};
export default ReportListViewTab;

ReportListViewTab.propTypes = {
  selectedDevice: PropTypes.array,
  CustomToggle: PropTypes.func,
  setActiveAccordion: PropTypes.func,
  activeAccordion: PropTypes.object,
  updateSelectedFilters: PropTypes.func,
  updateReportData: PropTypes.func,
  durationErr: PropTypes.object,
  selectedDuration: PropTypes.array,
  currentRoute: PropTypes.string,
};
